/**
 * Shared helpers for Jira status display, sorting, and mapping.
 */
import type { IJiraStatus, IStatusMapping } from '@skystone/shared';

type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'error';

/**
 * Derive the default internal status from a Jira status category key.
 */
export function getDefaultInternalStatus(status: IJiraStatus): IStatusMapping['internalStatus'] {
  switch (status.statusCategory.key) {
    case 'indeterminate':
      return 'Ongoing';
    case 'done':
      return 'Done';
    default:
      return 'Backlog';
  }
}

/**
 * Choose a Badge variant based on the Jira status category.
 */
export function getCategoryBadgeVariant(status: IJiraStatus): BadgeVariant {
  switch (status.statusCategory.key) {
    case 'new':
      return 'warning';
    case 'indeterminate':
      return 'default';
    case 'done':
      return 'success';
    default:
      return 'secondary';
  }
}

/**
 * Choose a Badge variant based on our internal status value.
 */
export function getInternalStatusBadgeVariant(
  internalStatus: IStatusMapping['internalStatus']
): BadgeVariant {
  switch (internalStatus) {
    case 'Backlog':
      return 'warning';
    case 'Ongoing':
      return 'default';
    case 'Done':
      return 'success';
    default:
      return 'secondary';
  }
}

/** Rank map for internal statuses used by sort comparators. */
const INTERNAL_STATUS_RANK: Record<IStatusMapping['internalStatus'], number> = {
  Backlog: 0,
  Ongoing: 1,
  Done: 2,
};

/**
 * Compute a numeric pin-rank for a status name so that well-known
 * statuses sort to predictable positions (Backlog first, Done/Closed last).
 */
function pinRank(name: string): number {
  const n = name.trim().toLowerCase();
  if (n === 'backlog') return -100;
  if (n === 'done') return 100;
  if (n === 'closed') return 101;
  return 0;
}

/**
 * Compare two IStatusMapping objects for sorting:
 *   1. Pin rank (Backlog first, Done/Closed last)
 *   2. Internal status rank (Backlog < Ongoing < Done)
 *   3. Alphabetical by status name
 */
export function compareStatusMappings(a: IStatusMapping, b: IStatusMapping): number {
  const aPin = pinRank(a.statusName);
  const bPin = pinRank(b.statusName);
  if (aPin !== bPin) return aPin - bPin;

  const aInternal = INTERNAL_STATUS_RANK[a.internalStatus];
  const bInternal = INTERNAL_STATUS_RANK[b.internalStatus];
  if (aInternal !== bInternal) return aInternal - bInternal;

  return a.statusName.localeCompare(b.statusName);
}

/**
 * Compare two effective-mapping objects (IJiraStatus + IStatusMapping pairs)
 * using the same algorithm as compareStatusMappings but reading from the
 * wrapped structure used in ProjectSettingsPage.
 */
export function compareEffectiveMappings(
  a: { status: IJiraStatus; mapping: IStatusMapping },
  b: { status: IJiraStatus; mapping: IStatusMapping }
): number {
  const aPin = pinRank(a.status.name);
  const bPin = pinRank(b.status.name);
  if (aPin !== bPin) return aPin - bPin;

  const aInternal = INTERNAL_STATUS_RANK[a.mapping.internalStatus];
  const bInternal = INTERNAL_STATUS_RANK[b.mapping.internalStatus];
  if (aInternal !== bInternal) return aInternal - bInternal;

  return a.status.name.localeCompare(b.status.name);
}
